import {
  AgentComplianceReviewDocumentQuery,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import {
  buildComplianceFieldHighlights,
  buildValidationFieldsMap,
  FieldsByContractDocument,
  getValidationItemFieldIds,
  ValidationField,
} from '~/common/pages/Compliance/utils/fieldHighlights.utils';
import { getValidationResultsForDocument } from '~/common/pages/Compliance/utils/validation.utils';
import {
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
} from '../components/types';

export type { ValidationField };

type AgentDocumentDetail = NonNullable<
  AgentComplianceReviewDocumentQuery['agentComplianceReviewDocument']
>;

type ValidationData = Parameters<typeof getValidationResultsForDocument>[1];

export interface AgentDocumentValidation {
  /** Failed checks, in the right-rail "suggestion" framing. */
  suggestions: ComplianceSuggestion[];
  /** Passed checks. */
  successfulChecks: ComplianceSuccessfulCheck[];
  /** Field ids to paint as failed (yellow warnings on the agent page). */
  errorFieldIds: string[];
  /** Field ids to paint as passed (green). */
  successFieldIds: string[];
  /** Per-field message for failed fields. */
  fieldMessages: Record<string, string>;
  /** Page → field boxes for the PDF overlay. */
  validationFieldsMap: Map<number, ValidationField[]>;
}

const EMPTY: AgentDocumentValidation = {
  suggestions: [],
  successfulChecks: [],
  errorFieldIds: [],
  successFieldIds: [],
  fieldMessages: {},
  validationFieldsMap: new Map(),
};

/**
 * Pure derivation of the agent compliance review per-document display —
 * suggestions, successful checks, and PDF field highlights — from the RAW
 * document detail returned by the backend (repositioned `fields`,
 * `contractDocuments`, `contractDocumentPages`, and the raw
 * `validationResults`/`aiValidationResults` arrays).
 *
 * It runs the SAME shared `getValidationResultsForDocument` the compliance
 * drawer + extension use (AI-prioritized split) and the shared
 * `buildComplianceFieldHighlights`/`getValidationItemFieldIds` field-matching,
 * so the agent page never forks that transformation onto the backend.
 *
 * Exported as a plain function (not just the hook) so the checklist-level
 * resubmit can derive each tab's suggestions from imperatively-fetched detail
 * without re-deriving the logic.
 */
export const deriveAgentDocumentValidation = (
  detail: AgentDocumentDetail | null | undefined
): AgentDocumentValidation => {
  if (!detail) {
    return EMPTY;
  }

  const fields = (detail.fields ?? {}) as FieldsByContractDocument;

  // The shared util reads these off a GetIngestionDocumentFragment-shaped
  // document + validation report data; we supply only the fields it touches
  // and cast (the same adapter pattern the extension hook uses).
  const documentLike = {
    documentId: detail.documentId,
    contractDocuments: detail.contractDocuments,
    // `fields` / `contractDocumentPages` are JSON scalars (typed `any` by
    // codegen); narrow to `unknown` before the adapter cast.
    fields: detail.fields as unknown,
    contractDocumentPages: detail.contractDocumentPages as unknown,
  } as unknown as GetIngestionDocumentFragment;

  // Key the synthetic document validation by the real validation uuid so the
  // derived items' `ingestionDocumentValidationId` matches the hidden-review
  // records (whose `ingestionDocumentValidationId` is that same uuid). This
  // lets both the USER-hidden filter (below, via the shared util) and the
  // AGENT-hidden marking key off a single, consistent id.
  const ingestionDocumentValidationId =
    detail.ingestionDocumentValidationId ?? detail.ingestionDocumentId;

  const validationData = {
    documentValidations: [
      {
        id: ingestionDocumentValidationId,
        validationResults: detail.validationResults as unknown,
        aiValidationResults: detail.aiValidationResults as unknown,
      },
    ],
  } as unknown as ValidationData;

  // Hidden reviews come back on the detail (cast — JSON-ish typed by codegen).
  // USER-hidden reviews are filtered OUT of the suggestions (passed to the
  // shared util); AGENT-hidden reviews are KEPT but marked ignored (rendered
  // crossed-out with an Undo affordance).
  const hiddenReviews = detail.hiddenValidationReviews ?? [];
  const userHidden = hiddenReviews.filter((review) => review.source === 'USER');
  const agentHidden = hiddenReviews.filter(
    (review) => review.source === 'AGENT'
  );

  // Adapt USER-hidden reviews to the shape the shared util matches against
  // (an `IngestionDocumentValidationErrorReviewFragment`-like record), keying
  // `ingestionDocumentValidation.id` on the same validation uuid used above.
  const userHiddenReviews = userHidden.map(
    (review) =>
      ({
        ruleUuid: review.ruleUuid ?? undefined,
        contractDocumentUuid: review.contractDocumentUuid ?? undefined,
        ingestionDocumentValidation: {
          id: review.ingestionDocumentValidationId ?? undefined,
        },
        isHidden: true,
      }) as unknown as IngestionDocumentValidationErrorReviewFragment
  );

  const { errors, successes } = getValidationResultsForDocument(
    documentLike,
    validationData,
    undefined,
    userHiddenReviews
  );

  const highlights = buildComplianceFieldHighlights(errors, successes, fields);

  const suggestions: ComplianceSuggestion[] = errors.map((error) => {
    // An AGENT-hidden review marks this suggestion as ignored (crossed-out +
    // Undo). Match the same way the USER-hidden filter does: rule + contract
    // document + the validation uuid carried on both sides.
    const agentReview = agentHidden.find(
      (review) =>
        review.ruleUuid === error.ruleUuid &&
        review.contractDocumentUuid === error.contractDocumentUuid &&
        (review.ingestionDocumentValidationId ?? undefined) ===
          (error.ingestionDocumentValidationId ?? undefined)
    );

    return {
      message: error.message,
      pageNumbers: error.pageNumbers,
      ruleUuid: error.ruleUuid,
      isAddedByUser: false,
      fieldIds: getValidationItemFieldIds(error, fields),
      // The owning contract document for the per-row "Ignore" hide. Sourced
      // from the raw `ValidationItem.contractDocumentUuid` populated by
      // `getValidationResultsForDocument` (each result's own
      // `contractDocumentUuid`). Left undefined when the error has no single
      // owning contract document — the backend find-or-create tolerates it.
      contractDocumentUuid: error.contractDocumentUuid,
      isIgnored: agentReview != null,
      reviewUuid: agentReview?.reviewUuid ?? null,
    };
  });

  const successfulChecks: ComplianceSuccessfulCheck[] = successes.map(
    (success) => ({
      message: success.message,
      pageNumbers: success.pageNumbers,
      ruleUuid: success.ruleUuid,
      fieldIds: getValidationItemFieldIds(success, fields),
    })
  );

  return {
    suggestions,
    successfulChecks,
    ...highlights,
    validationFieldsMap: buildValidationFieldsMap(fields),
  };
};

/**
 * Memoized hook wrapper around {@link deriveAgentDocumentValidation} for the
 * selected document's live detail.
 */
export const useAgentDocumentValidation = (
  detail: AgentDocumentDetail | null | undefined
): AgentDocumentValidation =>
  useMemo(() => deriveAgentDocumentValidation(detail), [detail]);
